import React, { useState, useEffect, useRef, useMemo } from "react";
import { useProfileParams } from "../../../../../../utils/common";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import DonutChart, {
  DonutChartSegment,
} from "../../../../../common/DonutChart/DonutChart";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import { useTranslation } from "react-i18next";
import ViewModeSwitcher, {
  ViewMode,
} from "../../../../../common/ViewModeSwitcher/ViewModeSwitcher";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import { useGetAGCAIDetailsMutation } from "../../../../../../redux/services/agcAIAPI";
import detailsStyles from "./TopProducts.module.scss";
import CategoryPerformanceView, { BusinessLine } from "../../Shared/CategoryPerformanceView/CategoryPerformanceView";

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const TopProducts: React.FC<SummaryProp> = ({
  summary,
  loading = false,
}) => {
  // Display API-provided segments when available; otherwise show nothing (0 fallback is implicit per segment values)
  const segments: DonutChartSegment[] = Array.isArray(summary?.segments)
    ? summary!.segments
    : [];
  const { t } = useTranslation();

  return (
    <KPITile
      title={t("top_products")}
      dataTestId="top_products_tile"
      loading={loading}
      className={detailsStyles.center_content}
    >
      <div className={detailsStyles.tile_content}>
        <DonutChart
          size={125}
          strokeWidth={20}
          legendGap={24}
          segments={segments}
          showLegend
          isPortfolio
        />
      </div>
    </KPITile>
  );
};

export const TopProductsDetails: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = "top10Product", onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("left");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("perfYear");
  
  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp, isLoading: isLoadingAiSummary }] = useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const kpiData = useMemo(() => {
    if (detailsResponse?.body?.kpis) {
      return detailsResponse.body.kpis.find((k: any) => k.kpiCode === kpiCode);
    }
    return null;
  }, [detailsResponse, kpiCode]);

  const py25Data: BusinessLine[] | undefined = useMemo(() => {
    const tableRows = kpiData?.detail?.ytdView?.chart?.[0]?.chartDetail?.tableDetail?.body?.rows;
    if (Array.isArray(tableRows)) {
      return tableRows
        .filter((row: any) => row[0]?.groupName !== "All")
        .map((row: any, index: number) => {
          const groupName = row[0]?.groupName;
          const category = groupName 
            ? groupName.toLowerCase().split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
            : "Unknown";
            
          return {
            id: index.toString(),
            name: row[0]?.kpiValue || "Unknown",
            category: category === "Other" ? "Other" : category, // Ensure "Other" matches tab if case differs? Tabs has "Other".
            revenue: row[3]?.kpiValue || 0,
            bvValue: row[4]?.kpiValue || 0,
            changePercentage: row[1]?.kpiValue || 0
          };
        });
    }
    return [];
  }, [kpiData]);

  const monthlyViewData = useMemo(() => {
    if (!kpiData?.detail?.monthlyView?.chart) return null;
    const chartData = Array.isArray(kpiData.detail.monthlyView.chart) ? kpiData.detail.monthlyView.chart[0] : kpiData.detail.monthlyView.chart;
    
    const chartObj = JSON.parse(JSON.stringify(chartData));

    if (chartObj?.chartDetail?.tableDetail?.body?.rows) {
      chartObj.chartDetail.tableDetail.body.rows.forEach((row: any) => {
        if (row[0] && row[0].rowComponent) {
          row[0].children = row[0].rowComponent;
        }
      });
    }

    return { detail: { chart: chartObj } };
  }, [kpiData]);

  const yearlyViewData = useMemo(() => {
    if (!kpiData?.detail?.annuallyView?.chart) return null;
    const chartData = Array.isArray(kpiData.detail.annuallyView.chart) ? kpiData.detail.annuallyView.chart[0] : kpiData.detail.annuallyView.chart;
    
    const chartObj = JSON.parse(JSON.stringify(chartData));

    if (chartObj?.chartDetail?.tableDetail?.body?.rows) {
      chartObj.chartDetail.tableDetail.body.rows.forEach((row: any) => {
        if (row[0] && row[0].rowComponent) {
          row[0].children = row[0].rowComponent;
        }
      });
    }

    return { detail: { chart: chartObj } };
  }, [kpiData]);

  const aiRaw = aiDetailsResp?.body;
  const aiResolved = typeof aiRaw === "string" ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || "";
  const aiContent = aiResolved ? String(aiResolved).slice(0, 300).concat("...") : "";

  const perfYear = "PY 25";
  const periodItems = useMemo(
    () => [
      { code: "perfYear", label: perfYear },
      { code: "monthly", label: t("monthly") },
      { code: "yearly", label: t("yearly") },
    ],
    [t],
  );

  const headerContent = (
    <div className={detailsStyles.header_content}>
      <div className={detailsStyles.tile_header}>
        <div className={detailsStyles.title}>{t("topproducts.title")}</div>
        <div className={detailsStyles.desc}>{t("topproducts.description")}</div>
      </div>
      <div className={detailsStyles.chart_view_wrapper}>
        <ViewModeSwitcher
          mode={viewMode}
          onChangeMode={setViewMode}
          disabled={false}
          showSwitch={false}
          selectedTimeRangeCode={selectedPeriod}
          onSelectTimeRange={setSelectedPeriod}
          timeRanges={periodItems}
        />
      </div>
    </div>
  );

  const leftContent = (
    <div className={detailsStyles.left_content_wrapper}>
      {selectedPeriod === "perfYear" ? (
        <CategoryPerformanceView
          viewType="chart"
          showTabs={true}
          title={t("topproducts.py_subtitle")}
          data={py25Data}
          description={t("categorymix.sales_performance_description")}
        />
      ) : selectedPeriod === "monthly" ? (
        <CategoryPerformanceView
          viewType="table"
          data={monthlyViewData}
          title={t("topproducts.monthly_subtitle")}
          description={t("categorymix.sales_performance_description")}
          highlightColumns={[]}
          groupedColumnClass
          tableFallbackValue=""
        />
      ) : (
        <CategoryPerformanceView
          viewType="table"
          data={yearlyViewData}
          title={t("topproducts.monthly_subtitle")}
          description={t("categorymix.sales_performance_description")}
          highlightColumns={[1, 3]}
          groupedColumnClass
          tableFallbackValue=""
        />
      )}
    </div>
  );

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t("ai_summary")}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  if (isLoading) return <Skeleton height="400px" />;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="top_products_detail"
    />
  );
};

export default TopProducts;
